import React from 'react';
import _get from 'lodash/get';
import _isString from 'lodash/isString';
import _isObject from 'lodash/isObject';

import ErrorPage from 'views/Errors/ErrorPage';
import NotFound from 'views/Errors/NotFound';
import logError from 'utils/extra/logError';
import { connectError } from 'utils/connectors/AppConnectors';
import { isChunkLoadError, attemptChunkReload } from 'utils/extra/chunkReload';

const FALLBACK_MESSAGE = 'Something went wrong. Please try again later.';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: this.props.hasError };
  }
  componentDidUpdate(prevProps) {
    // Dependent on AuthContext
    if (prevProps.hasError !== this.props.hasError) {
      this.setState({
        hasError: this.props.hasError,
      });
      return;
    }
    // `resetKey` is the router location key (see src/App.js). Navigating away
    // from a route that threw clears the fallback UI, so a stuck chunk/render
    // error on one route doesn't strand the user for the rest of the session.
    // Guarded by !this.props.hasError so this can never fight the
    // AuthContext-driven error state handled above.
    if (
      prevProps.resetKey !== this.props.resetKey &&
      !this.props.hasError &&
      this.state.hasError
    ) {
      this.setState({ hasError: false });
    }
  }
  componentDidCatch(error, info) {
    const errorData = { componentStack: info.componentStack };

    if (isChunkLoadError(error)) {
      if (attemptChunkReload()) {
        // A reload is already in flight for this build; don't paint the
        // fallback UI in the moment before the page navigates away.
        return;
      }
      // Already retried once this session and the chunk is still missing -
      // fall through to the normal fallback UI rather than reload forever.
      errorData.chunk = true;
    }

    this.setState({ hasError: true });
    logError(error, errorData);
  }

  render() {
    const { errorObject, children } = this.props;
    const displayMessage = _get(errorObject, 'displayMessage', null);
    const statusCode = _get(errorObject, 'statusCode', null);
    const method = _get(errorObject, 'method', null);

    if (!this.state.hasError) {
      return children;
    }

    if (_isObject(errorObject) && statusCode === 404 && method === 'get') {
      return <NotFound />;
    }

    const message = _isString(errorObject)
      ? errorObject
      : _isObject(errorObject)
        ? _isString(displayMessage)
          ? displayMessage
          : _isObject(displayMessage)
            ? _get(displayMessage, 'message', FALLBACK_MESSAGE)
            : FALLBACK_MESSAGE
        : FALLBACK_MESSAGE;

    return <ErrorPage allowRefresh message={message} />;
  }
}

export default connectError(ErrorBoundary);
